import BluetoothOutlineIcon from '@assets/icons/bluetooth-outline.svg';
import i18n from '@/utils/i18n';
import InfoIcon from '@assets/icons/info.svg';
import InvoiceIcon from '@assets/icons/invoice.svg';
import MenuHorizontalIcon from '@assets/icons/menu-horizontal.svg';
import OfflineModeIcon from '@assets/icons/offline-mode.svg';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import {
  adaptCreditRequestLineItemToBatchInput,
  useCreateCreditRequest,
} from '@/hooks/use-create-credit-request';
import {
  adaptMinimalScanningVariantPackToStandardFragment,
  trackScanningEvent,
} from '@/utils/scanning';
import {
  AlertLevel,
  CreditRequestAlert,
  CreditRequestScanScreen_SearchVariantPacksFieldsFragment,
  CreditRequestScanScreen_SearchVariantPacksQuery,
  CreditRequestScanScreen_SearchVariantPacksQueryVariables,
  ItemsContext_VariantsPackFieldsFragment,
  VariantPackCreditRequestDataFieldsFragment,
} from '@/types';
import { APP_MODAL_ROUTES, CREDIT_REQUEST_ROUTES } from '@/navigators/routes';
import { AppModalStackParams } from '@/navigators/types';
import { BaseActionSheet } from '@/components/base-action-sheet';
import {
  BORDER_RADIUS_LARGE,
  SCREEN_PADDING_SMALL,
  shadow,
  TYPOGRAPHY,
} from '@/styles';
import { Button } from '@/components/button';
import { CollapsibleAlerts } from '@/components/collapsible-alerts';
import { createStyles } from '@/contexts/theme-context';
import { CreditPolicy } from '@/components/credit-request/credit-policy';
import { CreditRequestActionSheet } from '@/components/credit-request/credit-request-action-sheet';
import { CreditRequestBasketItem } from '@/components/credit-request/credit-request-basket-item';
import { CreditRequestBasketStorageAdapter } from '@/adapters/credit-request';
import { CreditRequestBatchLineItem } from '@/contexts/credit-request-context-types';
import { CreditRequestVariantPackListItem } from '@/components/credit-request/credit-request-variant-pack-list-item';
import { formatCurrency, getCountryCurrencyCode } from '@/utils/money';
import { ItemListOption } from '@/contexts/item-context-types';
import {
  KeyboardAwareRecyclerList,
  ListSection,
} from '@/components/recycler-list';
import {
  ScannerType,
  ScanningEventSource,
  ScanningSearchMethod,
} from '@/components/scanner/scanner-types';
import { ScanPageContainer } from '@/components/scanner/scan-page-container';
import { SEARCH_VP_FOR_CREDIT_REQUEST_QUERY } from '@/hooks/credit-requests/credit-request-queries';
import { StackNavigationProp } from '@react-navigation/stack';
import { Text, TouchableOpacity, View } from 'react-native';
import { Type } from '@/components/toast';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useAdaptVariantPackToItemListOption } from '@/contexts/item-context-utils';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useDebouncedCallback } from 'use-debounce';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useItemQueryVariables } from '@/hooks/use-item-query-variables';
import { useLazyQuery } from '@/hooks/use-lazy-query';
import { useNavigation } from '@react-navigation/native';
import { useOfflineCreditRequestData } from '@/hooks/use-offline-data';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { useScannerSearch } from '@/hooks/use-scanner-search';
import { useToasts } from '@/contexts/toast-context';
import { useValidateCreditRequest } from '@/components/credit-request/hooks/use-validate-credit-request';

type NavigationType = StackNavigationProp<
  AppModalStackParams,
  APP_MODAL_ROUTES.CreditRequest
>;

const HIT_SLOP = {
  bottom: 8,
  left: 8,
  right: 8,
  top: 8,
};

const DEBOUNCE_DELAY_MS = 250;

export const CreditRequestScanScreen: React.FC = () => {
  const adaptVariantPackToItemListOption =
    useAdaptVariantPackToItemListOption();
  const createCreditRequestFromLineItems = useCreateCreditRequest();
  const validateCreditRequest = useValidateCreditRequest();
  const itemQueryVariables = useItemQueryVariables();
  const { navigate, goBack } = useNavigation<NavigationType>();
  const { showToast, showErrorToast } = useToasts();
  const [styles, theme] = useStyles();
  const {
    display_guarantee_information: displayGuaranteeInformation,
    credit_policy: creditPolicy,
  } = useDynamicConfigValue('credit_request_config') ?? {};
  const {
    creditRequestSettings,
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { chatUUIDs, countryCode, supplierUUID },
  } = useAuthenticatedUserContext();
  const currencyCode = countryCode
    ? getCountryCurrencyCode(countryCode)
    : undefined;

  const { searchOnDevice } = useScannerSearch();
  const isOfflineModeActive = useOfflineModeActive();
  const { data: offlineCreditRequestData } = useOfflineCreditRequestData(
    restaurantUUID,
    supplierUUID,
  );

  const [variantPacksResults, setVariantPacksResults] = useState<
    CreditRequestScanScreen_SearchVariantPacksFieldsFragment[] | null
  >(null);

  const [creditRequestBatchLineItems, setCreditRequestBatchLineItems] =
    useState<CreditRequestBatchLineItem[]>([]);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [isMakingSelection, setIsMakingSelection] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showCreditPolicy, setShowCreditPolicy] = useState(false);
  const [showIneligibleItemInfo, setShowIneligibleItemInfo] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [validationAlerts, setValidationAlerts] = useState<
    CreditRequestAlert[] | null
  >(null);

  const codeRef = useRef<string>(null);
  const hydratedRef = useRef(false);

  const creditRequestStorageAdapter = useRef<CreditRequestBasketStorageAdapter>(
    new CreditRequestBasketStorageAdapter(restaurantUUID, supplierUUID),
  );

  const debouncedSave = useDebouncedCallback(
    async (items: CreditRequestBatchLineItem[]) => {
      await creditRequestStorageAdapter.current?.save(items);
    },
    DEBOUNCE_DELAY_MS,
  );

  useEffect(() => {
    const hydrateAndValidateBatch = async () => {
      try {
        const savedBatchLineItems =
          await creditRequestStorageAdapter.current?.fetch();
        if (hydratedRef.current || !savedBatchLineItems?.length) {
          return;
        }

        setCreditRequestBatchLineItems(savedBatchLineItems);
        hydratedRef.current = true;

        if (isOfflineModeActive) {
          return;
        }

        const { data } = await validateCreditRequest(
          chatUUIDs[0],
          adaptCreditRequestLineItemToBatchInput(savedBatchLineItems),
        );

        const alerts = data?.validateCreditRequest?.alerts ?? [];
        if (alerts.length > 0) {
          // Only filter ineligible items for negative alerts
          const ineligibleUUIDs = new Set(
            alerts.flatMap((alert) =>
              alert.alert_level === AlertLevel.Negative
                ? alert.ineligible_variant_pack_ids
                : [],
            ),
          );

          if (ineligibleUUIDs.size > 0) {
            setCreditRequestBatchLineItems(
              savedBatchLineItems.filter(
                (item) => !ineligibleUUIDs.has(item.uuid),
              ),
            );
          }

          // Use Warning level for alert banner styling
          setValidationAlerts(
            alerts.map((alert) => ({
              ...alert,
              alert_level: AlertLevel.Warning,
            })),
          );
        }
      } catch {
        // Do nothing
      }
    };

    hydrateAndValidateBatch();
    // Hydrate batch from storage and validate once per session
    // and when switching from offline mode
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOfflineModeActive]);

  useEffect(() => {
    debouncedSave(creditRequestBatchLineItems);
  }, [creditRequestBatchLineItems, debouncedSave]);

  useEffect(() => {
    return () => {
      debouncedSave.flush();
    };
  }, [debouncedSave]);

  const searchVariantPacksForCreditRequest = useLazyQuery<
    CreditRequestScanScreen_SearchVariantPacksQuery,
    CreditRequestScanScreen_SearchVariantPacksQueryVariables
  >(SEARCH_VP_FOR_CREDIT_REQUEST_QUERY);

  const commonScanEventProperties = useMemo(
    () => ({
      restaurantUUID,
      source: ScanningEventSource.CreditRequest,
      supplierUUID,
      type: ScannerType.External,
    }),
    [restaurantUUID, supplierUUID],
  );

  const invoiceQuantityMap = useMemo(
    () =>
      creditRequestBatchLineItems.reduce<Record<string, number>>(
        (acc, item) => {
          const invoiceUUID = item.invoice?.metadata?.uuid;
          if (invoiceUUID) {
            acc[invoiceUUID] = (acc[invoiceUUID] || 0) + item.quantity;
          }
          return acc;
        },
        {},
      ),
    [creditRequestBatchLineItems],
  );

  const navigateToCreditRequestScreen = useCallback(
    (
      variantPack: ItemListOption,
      onSuccess: (batchLineItem: CreditRequestBatchLineItem) => void,
      searchResultItem: CreditRequestScanScreen_SearchVariantPacksFieldsFragment,
    ) => {
      navigate(APP_MODAL_ROUTES.CreditRequest, {
        params: {
          confirmOnExit: true,
          eligibleForCredit: searchResultItem.eligible_for_credit,
          guaranteed: searchResultItem.guaranteed,
          invoiceQuantityMap,
          itemDetail: variantPack,
          multiRequest: true,
          onSuccess,
        },
        screen: CREDIT_REQUEST_ROUTES.Base,
      });
    },
    [invoiceQuantityMap, navigate],
  );

  const addToBasket = useCallback(
    (creditRequestProps: CreditRequestBatchLineItem) => {
      setCreditRequestBatchLineItems([
        creditRequestProps,
        ...creditRequestBatchLineItems,
      ]); // add new items to the top
    },
    [creditRequestBatchLineItems],
  );

  const removeFromBasket = useCallback((uuid: string) => {
    setCreditRequestBatchLineItems((prev) =>
      prev.filter((item) => item.uuid !== uuid),
    );
  }, []);

  const updateItemQuantity = useCallback((uuid: string, quantity: number) => {
    setCreditRequestBatchLineItems((prev) =>
      prev.map((item) => (item.uuid === uuid ? { ...item, quantity } : item)),
    );
  }, []);

  const [totalPriceInMicros, quantityCount] = useMemo(() => {
    return creditRequestBatchLineItems.reduce(
      (acc, item) => {
        let runningPriceInMicros = acc[0];
        let runningQuantity = acc[1];

        runningPriceInMicros += item.quantity * item.priceInMicros;
        runningQuantity += item.quantity;

        return [runningPriceInMicros, runningQuantity];
      },
      [0, 0],
    );
  }, [creditRequestBatchLineItems]);

  const commonQueryVariables = useMemo(
    () => ({
      ...itemQueryVariables,
      restaurantUUID: restaurantUUID!,
      supplierUUID,
    }),
    [itemQueryVariables, restaurantUUID, supplierUUID],
  );

  const searchVariantPacksData = useCallback(
    async (code: string) => {
      try {
        const { data } = await searchVariantPacksForCreditRequest({
          variables: {
            ...commonQueryVariables,
            code,
          },
        });

        return data.searchVariantPacksForCreditRequest;
      } catch {
        return null;
      }
    },
    [commonQueryVariables, searchVariantPacksForCreditRequest],
  );

  const searchAllVariantPacks = useCallback(
    async (code: string, scannerType: ScannerType) => {
      const variantPacksData = await searchVariantPacksData(code);
      setVariantPacksResults(variantPacksData);

      if (!variantPacksData) {
        setIsMakingSelection(false);
        return;
      }

      trackScanningEvent({
        ...commonScanEventProperties,
        code,
        rawResults: variantPacksData.map((vp) => vp.variant_pack?.uuid ?? ''),
        searchMethod: ScanningSearchMethod.OffDevice,
        type: scannerType,
      });

      if (variantPacksData.length === 1 && variantPacksData[0].variant_pack) {
        navigateToCreditRequestScreen(
          adaptVariantPackToItemListOption(variantPacksData[0].variant_pack),
          (batchLineItem) => {
            addToBasket(batchLineItem);
            setIsMakingSelection(false);
          },
          variantPacksData[0],
        );
      } else if (variantPacksData.length === 0) {
        setErrorText(i18n.t('scan.no_results'));
        setIsMakingSelection(false);
      }
    },
    [
      adaptVariantPackToItemListOption,
      addToBasket,
      commonScanEventProperties,
      navigateToCreditRequestScreen,
      searchVariantPacksData,
    ],
  );

  const searchByText = useCallback(
    async (code: string, scannerType: ScannerType) => {
      // Apollo doesn't provide a mechanism to cancel an in-flight query so
      // this ensures that results from the latest query are displayed
      if (codeRef.current === code) {
        try {
          setIsMakingSelection(true);
          setIsSearching(true);
          await searchAllVariantPacks(code, scannerType);
        } catch {
          setVariantPacksResults(null);
          setErrorText(i18n.t('toast.standard_error'));
        } finally {
          setIsSearching(false);
        }
      }
    },
    [searchAllVariantPacks],
  );

  const clearResults = useCallback(() => {
    setIsMakingSelection(false);
    setVariantPacksResults(null);
    setErrorText(null);
  }, []);

  const [offlineCreditRequestEligibilityMap, offlineCreditRequestVariantPacks] =
    useMemo(() => {
      const data =
        offlineCreditRequestData?.getCreditRequestVariantPackEligibility
          .variant_pack_credit_request_data || [];

      return data.reduce<
        [
          Record<string, VariantPackCreditRequestDataFieldsFragment>,
          ItemsContext_VariantsPackFieldsFragment[],
        ]
      >(
        ([eligibilityMap, variantPacks], creditRequestEligibility) => {
          const vpUUID = creditRequestEligibility.variant_pack!.uuid;
          eligibilityMap[vpUUID] = creditRequestEligibility;

          variantPacks.push(
            adaptMinimalScanningVariantPackToStandardFragment(
              creditRequestEligibility.variant_pack!,
            ),
          );

          return [eligibilityMap, variantPacks];
        },
        [{}, []],
      );
    }, [offlineCreditRequestData]);

  const creditRequestSearchOnDevice = useCallback(
    async (code: string) => {
      setIsMakingSelection(true);

      if (offlineCreditRequestVariantPacks.length === 0) {
        setErrorText(i18n.t('scan.no_results'));
        setIsMakingSelection(false);
        return;
      }

      const onDeviceResults = searchOnDevice(
        code,
        offlineCreditRequestVariantPacks,
        true,
        // Credit requests must always show a chooser on a multi-match, even
        // when order-scan lowest-UOM auto-select is enabled for this chat.
        true,
      );

      const creditRequestResults: CreditRequestScanScreen_SearchVariantPacksFieldsFragment[] =
        onDeviceResults.map((variantPack) => {
          const variantPackEligibilityData =
            offlineCreditRequestEligibilityMap[variantPack.uuid];

          return {
            // All offline variant packs are eligible for credit and
            // will be validated for eligibility when submitting while online
            eligible_for_credit: true,
            guaranteed: Boolean(
              variantPackEligibilityData.variant_pack!.credit_request_config
                ?.guaranteed,
            ),
            last_fulfillment_date:
              variantPackEligibilityData.latest_invoice?.date,
            variant_pack: variantPack,
          };
        });

      setVariantPacksResults(creditRequestResults);

      if (creditRequestResults.length === 1) {
        const creditRequestResult = creditRequestResults[0];
        navigateToCreditRequestScreen(
          adaptVariantPackToItemListOption(creditRequestResult.variant_pack!),
          (batchLineItem) => {
            addToBasket(batchLineItem);
            setIsMakingSelection(false);
          },
          creditRequestResult,
        );
      } else if (creditRequestResults.length === 0) {
        setErrorText(i18n.t('scan.no_results'));
        setIsMakingSelection(false);
      }
    },
    [
      adaptVariantPackToItemListOption,
      addToBasket,
      navigateToCreditRequestScreen,
      offlineCreditRequestEligibilityMap,
      offlineCreditRequestVariantPacks,
      searchOnDevice,
    ],
  );

  const searchResults = useCallback(
    async (updatedText: string, scannerType: ScannerType) => {
      setErrorText(null);
      codeRef.current = updatedText;

      if (isOfflineModeActive) {
        await creditRequestSearchOnDevice(updatedText);
      } else {
        await searchByText(updatedText, scannerType);
      }
    },
    [creditRequestSearchOnDevice, isOfflineModeActive, searchByText],
  );

  const creditRequestBasketSections: ListSection<CreditRequestBatchLineItem>[] =
    useMemo(() => {
      if (creditRequestBatchLineItems.length === 0) {
        return [];
      }

      const itemsByInvoiceNumber = creditRequestBatchLineItems.reduce<
        Record<string, CreditRequestBatchLineItem[]>
      >((acc, item) => {
        const invoiceNumber = item.invoice?.value
          ? i18n.t('invoice_number', {
              number: item.invoice.value,
            })
          : i18n.t('no_invoice');
        if (!acc[invoiceNumber]) {
          acc[invoiceNumber] = [];
        }
        acc[invoiceNumber].push(item);
        return acc;
      }, {});

      return Object.keys(itemsByInvoiceNumber).map((invoiceNumber) => ({
        data: itemsByInvoiceNumber[invoiceNumber],
        key: invoiceNumber,
      }));
    }, [creditRequestBatchLineItems]);

  const searchSections = useMemo(() => {
    if (!variantPacksResults || variantPacksResults.length === 0) {
      return [];
    }

    const { eligible, ineligible } = variantPacksResults.reduce<{
      eligible: ItemsContext_VariantsPackFieldsFragment[];
      ineligible: ItemsContext_VariantsPackFieldsFragment[];
    }>(
      (acc, { eligible_for_credit, variant_pack }) => {
        if (!variant_pack) {
          return acc;
        }

        if (eligible_for_credit) {
          acc.eligible.push(variant_pack);
        } else {
          acc.ineligible.push(variant_pack);
        }
        return acc;
      },
      { eligible: [], ineligible: [] },
    );

    const sections: ListSection<
      ItemsContext_VariantsPackFieldsFragment,
      { isEligible: boolean }
    >[] = [];

    if (eligible.length > 0) {
      sections.push({
        data: eligible,
        key: isOfflineModeActive ? null : i18n.t('credit_request.eligible'),
        metadata: { isEligible: true },
      });
    }

    if (ineligible.length > 0) {
      sections.push({
        data: ineligible,
        key: i18n.t('credit_request.not_eligible'),
        metadata: { isEligible: false },
      });
    }

    return sections;
  }, [isOfflineModeActive, variantPacksResults]);

  const variantPackResultMap = useMemo(() => {
    return (variantPacksResults ?? []).reduce<
      Record<string, CreditRequestScanScreen_SearchVariantPacksFieldsFragment>
    >((acc, result) => {
      const uuid = result.variant_pack?.uuid;
      if (uuid) {
        acc[uuid] = result;
      }
      return acc;
    }, {});
  }, [variantPacksResults]);

  return (
    <ScanPageContainer
      bottomAction={
        !isMakingSelection && creditRequestBatchLineItems.length > 0 ? (
          <View style={styles.summaryContainer}>
            <View style={styles.pageFooterRow}>
              <Typography
                color={theme.sk.color.text.primary}
                variant="paragraph-base"
              >
                {i18n.t(
                  quantityCount === 1 ? 'unit_count' : 'unit_count_plural',
                  {
                    count: quantityCount,
                  },
                )}
              </Typography>
              <Typography
                color={theme.sk.color.text.primary}
                variant="paragraph-base-600"
              >
                {formatCurrency(totalPriceInMicros, currencyCode)}
              </Typography>
            </View>
            <Button
              disabled={isSubmitting || isOfflineModeActive}
              onPress={async () => {
                try {
                  setIsSubmitting(true);
                  await createCreditRequestFromLineItems(
                    creditRequestBatchLineItems,
                  );
                  await creditRequestStorageAdapter.current?.remove();
                  showToast({
                    content: i18n.t('credit_request.toast.success'),
                    type: Type.POSITIVE,
                  });
                  goBack();
                } catch (err) {
                  showErrorToast(
                    err instanceof Error ? err.message : undefined,
                  );
                  setIsSubmitting(false);
                }
              }}
              trackingData={{ name: 'credit_request_scan_screen.submit' }}
            >
              {i18n.t('credit_request_scan.screen.submit')}
            </Button>
            {isOfflineModeActive ? (
              <View style={styles.offlineMode}>
                <OfflineModeIcon />
                <Typography variant="paragraph-md">
                  {i18n.t('credit_request.offline_mode_banner')}
                </Typography>
              </View>
            ) : null}
          </View>
        ) : null
      }
      errorText={errorText}
      navigationHeaderProps={{
        actions:
          creditRequestBasketSections.length > 0
            ? [
                {
                  Icon: MenuHorizontalIcon,
                  onPress: () => setShowActions(true),
                  testID: 'credit-request-menu-button',
                },
              ]
            : undefined,
      }}
      onClearResults={clearResults}
      searchByCode={searchResults}
    >
      {(scannerType) => (
        <>
          {isMakingSelection ? (
            <View
              style={[
                styles.resultsContainer,
                scannerType === ScannerType.External
                  ? styles.resultsContainerExternal
                  : null,
              ]}
            >
              {isSearching ? (
                <View style={[styles.searchLoading, styles.flex]}>
                  <ActivityIndicator />
                  <Typography variant="paragraph-base">
                    {i18n.t('external_scanner.searching_state')}
                  </Typography>
                </View>
              ) : (
                <KeyboardAwareRecyclerList
                  alwaysBounceVertical={false}
                  estimatedItemSize={80}
                  keyboardDismissMode="on-drag"
                  keyboardShouldPersistTaps="handled"
                  listItemSeparatorProps={{
                    style: styles.itemSeparator,
                  }}
                  renderItem={({ item }) => {
                    const option = adaptVariantPackToItemListOption(item);
                    const variantPackResult = variantPackResultMap[item.uuid];
                    const isEligible = variantPackResult.eligible_for_credit;
                    const mostRecentInvoiceDate =
                      variantPackResult.last_fulfillment_date;
                    const guaranteed = displayGuaranteeInformation
                      ? variantPackResult.guaranteed
                      : undefined;

                    return isEligible ||
                      creditRequestSettings?.request_ineligible_items_enabled ? (
                      <TouchableOpacity
                        accessibilityRole="button"
                        onPress={() => {
                          navigateToCreditRequestScreen(
                            option,
                            (batchLineItem) => {
                              addToBasket(batchLineItem);
                              setIsMakingSelection(false);
                            },
                            variantPackResult,
                          );
                        }}
                        testID="credit-request-eligible-item"
                      >
                        <CreditRequestVariantPackListItem
                          guaranteed={guaranteed}
                          mostRecentInvoiceDate={mostRecentInvoiceDate}
                          option={option}
                        />
                      </TouchableOpacity>
                    ) : (
                      <View
                        accessibilityRole="text"
                        accessibilityState={{ disabled: true }}
                        accessible
                        style={styles.ineligibleItem}
                        testID="credit-request-ineligible-item"
                      >
                        <CreditRequestVariantPackListItem
                          guaranteed={guaranteed}
                          mostRecentInvoiceDate={mostRecentInvoiceDate}
                          option={option}
                        />
                      </View>
                    );
                  }}
                  renderSectionHeader={(item, _target, index) => (
                    <View
                      style={[
                        styles.sectionHeader,
                        index > 0 ? styles.additionalPadding : null,
                      ]}
                    >
                      <Typography variant="paragraph-md">
                        {item.title}
                      </Typography>
                      {!item.metadata?.isEligible &&
                      creditPolicy &&
                      creditPolicy.length > 0 ? (
                        <TouchableOpacity
                          accessibilityRole="button"
                          hitSlop={HIT_SLOP}
                          onPress={() => setShowCreditPolicy(true)}
                          testID="ineligible-items-info-icon"
                        >
                          <InfoIcon
                            height={16}
                            stroke={theme.sk.color.icon.tertiary}
                            width={16}
                          />
                        </TouchableOpacity>
                      ) : null}
                    </View>
                  )}
                  sections={searchSections}
                  stickyHeaderIndices={[]}
                  testID="credit-request-all-results"
                />
              )}
            </View>
          ) : (
            <View style={styles.flex}>
              {validationAlerts && validationAlerts.length > 0 ? (
                <CollapsibleAlerts
                  alerts={validationAlerts}
                  containerStyle={styles.alertContainer}
                />
              ) : null}
              {creditRequestBasketSections.length > 0 ? (
                <KeyboardAwareRecyclerList
                  alwaysBounceVertical={false}
                  contentContainerStyle={styles.batchListContainer}
                  estimatedItemSize={180}
                  keyboardDismissMode="on-drag"
                  keyboardShouldPersistTaps="handled"
                  renderItem={({ item }) => (
                    <CreditRequestBasketItem
                      batchLineItem={item}
                      key={item.uuid}
                      maxQuantity={item.maxQuantity}
                      onInfoIconPress={() => setShowIneligibleItemInfo(true)}
                      removeFromBasket={removeFromBasket}
                      updateItemQuantity={updateItemQuantity}
                    />
                  )}
                  renderSectionHeader={(item) => (
                    <View style={styles.invoiceNumber}>
                      <InvoiceIcon
                        height={16}
                        stroke={theme.sk.color.icon.primary}
                        width={16}
                      />
                      <Typography variant="label-md">{item.title}</Typography>
                    </View>
                  )}
                  sections={creditRequestBasketSections}
                  shouldRenderSeparator={() => false}
                  stickyHeaderIndices={[]}
                  testID="credit-request-scan-screen"
                />
              ) : null}
              {creditRequestBatchLineItems.length === 0 ? (
                <View style={[styles.emptyState, styles.flex]}>
                  <BluetoothOutlineIcon
                    stroke={theme.sk.color.icon.secondary}
                  />
                  <Text style={styles.messageText}>
                    {i18n.t('credit_request_scan.screen.empty_state')}
                  </Text>
                </View>
              ) : null}
            </View>
          )}
          {showCreditPolicy && creditPolicy ? (
            <BaseActionSheet
              isVisible
              onClose={() => setShowCreditPolicy(false)}
            >
              <View style={styles.creditPolicyWrapper}>
                <View style={styles.creditPolicyHeader}>
                  <Typography variant="heading-lg">
                    {i18n.t('credit_policy')}
                  </Typography>
                </View>
                <CreditPolicy creditPolicy={creditPolicy} />
              </View>
              <Button
                onPress={() => {
                  setShowCreditPolicy(false);
                }}
                trackingData={{
                  name: 'credit_request_policy_button_ok',
                }}
              >
                {i18n.t('got_it')}
              </Button>
            </BaseActionSheet>
          ) : null}
          {showIneligibleItemInfo && creditPolicy ? (
            <BaseActionSheet
              isVisible
              onClose={() => setShowIneligibleItemInfo(false)}
            >
              <View style={styles.creditPolicyWrapper}>
                <View style={styles.creditPolicyHeader}>
                  <Typography variant="heading-lg">
                    {i18n.t('ineligible_item.title')}
                  </Typography>
                </View>
                <CreditPolicy creditPolicy={creditPolicy} />
              </View>
            </BaseActionSheet>
          ) : null}
          {showActions ? (
            <CreditRequestActionSheet
              onClear={async () => {
                setCreditRequestBatchLineItems([]);
                await creditRequestStorageAdapter.current?.remove();
              }}
              onClose={() => setShowActions(false)}
            />
          ) : null}
        </>
      )}
    </ScanPageContainer>
  );
};

const useStyles = createStyles((theme) => ({
  additionalPadding: {
    paddingTop: 24,
  },
  alertContainer: {
    marginBottom: 8,
    marginHorizontal: 8,
  },
  batchListContainer: {
    paddingHorizontal: SCREEN_PADDING_SMALL,
  },
  creditPolicyHeader: {
    marginBottom: 16,
  },
  creditPolicyWrapper: {
    marginBottom: 16,
  },
  emptyState: {
    alignItems: 'center',
    gap: 12,
    paddingTop: 48,
  },
  flex: {
    flex: 1,
  },
  ineligibleItem: {
    opacity: 0.4,
  },
  invoiceNumber: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 10,
    marginBottom: 24,
    marginTop: 8,
  },
  itemSeparator: {
    backgroundColor: theme.sk.color.divider.default,
    marginHorizontal: SCREEN_PADDING_SMALL,
  },
  messageText: {
    ...TYPOGRAPHY.medium,
    lineHeight: 24,
    paddingHorizontal: 24,
    textAlign: 'center',
  },
  offlineMode: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'center',
  },
  pageFooterRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: 16,
  },
  resultsContainer: {
    backgroundColor: theme.sk.color.bg.primary.default,
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between',
    marginTop: 4,
    paddingTop: 24,
  },
  resultsContainerExternal: {
    ...shadow({
      color: theme.colors.black,
      elevation: 10,
      opacity: 0.15,
      radius: BORDER_RADIUS_LARGE,
      x: 0,
      y: -4,
    }),
    borderTopLeftRadius: BORDER_RADIUS_LARGE,
    borderTopRightRadius: BORDER_RADIUS_LARGE,
  },
  searchLoading: {
    alignItems: 'center',
    gap: 16,
    justifyContent: 'center',
  },
  sectionHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
    paddingHorizontal: SCREEN_PADDING_SMALL,
  },
  summaryContainer: {
    gap: 16,
    paddingHorizontal: SCREEN_PADDING_SMALL,
  },
}));
